/*
 * Initial AU – small interface layer: section reveals, product rails, hero parallax.
 * No dependencies. Everything is skipped when the visitor prefers reduced motion.
 */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Reveal sections as they scroll into view ---------- */
  function initReveal(root) {
    var els = (root || document).querySelectorAll('[data-iau-reveal]:not(.is-in)');
    if (!els.length) return;
    if (reduce || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.05 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Sideways product rails ---------- */
  function initRails(root) {
    (root || document).querySelectorAll('[data-iau-rail]').forEach(function (rail) {
      if (rail.dataset.iauReady) return;
      rail.dataset.iauReady = '1';
      var track = rail.querySelector('.iau-rail-track');
      var bar = rail.querySelector('[data-iau-rail-bar]');
      var prev = rail.querySelector('[data-iau-rail-prev]');
      var next = rail.querySelector('[data-iau-rail-next]');
      if (!track) return;

      function update() {
        var max = track.scrollWidth - track.clientWidth;
        var visible = track.clientWidth / track.scrollWidth;
        var progress = max > 0 ? track.scrollLeft / max : 0;
        if (bar) {
          bar.style.width = Math.max(visible * 100, 12) + '%';
          bar.style.transform = 'translateX(' + (progress * (1 / Math.max(visible, 0.12) - 1) * 100) + '%)';
        }
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max - 2;
        rail.classList.toggle('is-static', max <= 2);
      }

      function step(dir) {
        var item = track.querySelector('.iau-rail-item');
        var amount = item ? item.getBoundingClientRect().width * Math.max(1, Math.floor(track.clientWidth / item.getBoundingClientRect().width) - 1) : track.clientWidth * 0.8;
        track.scrollBy({ left: dir * amount, behavior: reduce ? 'auto' : 'smooth' });
      }

      track.addEventListener('scroll', function () { requestAnimationFrame(update); }, { passive: true });
      window.addEventListener('resize', update);
      if (prev) prev.addEventListener('click', function () { step(-1); });
      if (next) next.addEventListener('click', function () { step(1); });
      update();
    });
  }

  /* ---------- Headlines marked data-iau-fit shrink so the longest line never overflows ---------- */
  function fitTitles() {
    document.querySelectorAll('[data-iau-fit]').forEach(function (h) {
      h.style.fontSize = '';
      var box = h.parentElement;
      if (!box) return;
      var max = box.clientWidth;
      var widest = 0;
      h.querySelectorAll('.iau-line > span').forEach(function (s) { widest = Math.max(widest, s.scrollWidth); });
      if (max > 0 && widest > max) {
        var size = parseFloat(window.getComputedStyle(h).fontSize);
        h.style.fontSize = Math.floor(size * (max / widest) * 0.98) + 'px';
      }
    });
  }
  var fitTimer = null;
  window.addEventListener('resize', function () {
    clearTimeout(fitTimer);
    fitTimer = setTimeout(fitTitles, 120);
  });

  /* ---------- Hero: products / photo drift slightly with the pointer ---------- */
  function initHero() {
    fitTitles();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitTitles);
    var heroes = Array.prototype.slice.call(document.querySelectorAll('[data-iau-hero]'));
    if (!heroes.length) return;
    requestAnimationFrame(function () {
      heroes.forEach(function (h) { h.classList.add('is-ready'); });
    });
    if (reduce || !window.matchMedia('(hover: hover) and (min-width: 992px)').matches) return;
    heroes.forEach(function (hero) {
      var tiles = hero.querySelectorAll('[data-depth]');
      if (!tiles.length) return;
      var raf = null;
      hero.addEventListener('pointermove', function (e) {
        var r = hero.getBoundingClientRect();
        var x = (e.clientX - r.left) / r.width - 0.5;
        var y = (e.clientY - r.top) / r.height - 0.5;
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () {
          tiles.forEach(function (t) {
            var d = parseFloat(t.getAttribute('data-depth')) || 1;
            t.style.setProperty('--px', (x * 18 * d).toFixed(1) + 'px');
            t.style.setProperty('--py', (y * 14 * d).toFixed(1) + 'px');
          });
        });
      });
      hero.addEventListener('pointerleave', function () {
        tiles.forEach(function (t) { t.style.setProperty('--px', '0px'); t.style.setProperty('--py', '0px'); });
      });
    });
  }

  /* ---------- Hero slideshow: swipe, dots, and a gentle auto-advance ---------- */
  function initHeroSlider() {
    document.querySelectorAll('[data-iau-hero-slider]').forEach(function (root) {
      var track = root.querySelector('[data-iau-hero-track]');
      var dots = Array.prototype.slice.call(root.querySelectorAll('[data-iau-hero-dot]'));
      if (!track || dots.length < 2) return;

      var slides = Array.prototype.slice.call(track.children);
      var current = 0;
      var timer = null;
      var wait = (parseFloat(root.getAttribute('data-iau-hero-autoplay')) || 7) * 1000;

      function mark(i) {
        current = i;
        dots.forEach(function (d, n) {
          d.classList.toggle('is-active', n === i);
          if (n === i) d.setAttribute('aria-current', 'true');
          else d.removeAttribute('aria-current');
        });
      }

      function goTo(i, smooth) {
        var slide = slides[i];
        if (!slide) return;
        track.scrollTo({ left: slide.offsetLeft - slides[0].offsetLeft, behavior: smooth === false || reduce ? 'auto' : 'smooth' });
      }

      // Which slide is in view: whichever is nearest the track's left edge.
      var settle = null;
      track.addEventListener('scroll', function () {
        clearTimeout(settle);
        settle = setTimeout(function () {
          var best = 0;
          var min = Infinity;
          slides.forEach(function (s, n) {
            var d = Math.abs(s.offsetLeft - slides[0].offsetLeft - track.scrollLeft);
            if (d < min) { min = d; best = n; }
          });
          if (best !== current) mark(best);
        }, 90);
      }, { passive: true });

      dots.forEach(function (d, n) {
        d.addEventListener('click', function () {
          stop();
          mark(n);
          goTo(n);
          start();
        });
      });

      function start() {
        if (reduce) return;
        stop();
        timer = setInterval(function () {
          if (document.hidden) return;
          goTo((current + 1) % slides.length);
        }, wait);
      }
      function stop() { if (timer) { clearInterval(timer); timer = null; } }

      root.addEventListener('pointerenter', stop);
      root.addEventListener('pointerleave', start);
      root.addEventListener('focusin', stop);
      root.addEventListener('focusout', function (e) { if (!root.contains(e.relatedTarget)) start(); });
      track.addEventListener('pointerdown', stop);
      document.addEventListener('visibilitychange', function () { if (document.hidden) stop(); else start(); });

      // The track keeps its scroll position across a resize, so put the current slide back in place.
      var rt = null;
      window.addEventListener('resize', function () {
        clearTimeout(rt);
        rt = setTimeout(function () { goTo(current, false); }, 150);
      });

      mark(0);
      start();
    });
  }

  /* ---------- Cart drawer and badge: never show a cart that is out of date ----------
   * The drawer and the header count are rendered with the page, so a page the browser
   * brings back from its back/forward cache still shows the cart as it was before the
   * last add — empty, in the worst case, even though the item is really in the cart.
   * Re-read the cart whenever the page comes back into view and whenever the drawer is
   * opened, and refresh the markup only when it is actually out of date. */
  function initCartSync() {
    var drawer = document.querySelector('.side-cart');
    if (!drawer || !window.fetch) return;

    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    var cartUrl = (window.btyGlobals && btyGlobals.cart_url) || '/cart';
    var busy = false;
    var known = null; // Cart total the drawer markup is known to reflect.

    function badgeCount() {
      var el = document.querySelector('.cart-item-count');
      if (!el) return null;
      var n = parseInt((el.textContent || '').trim(), 10);
      return isNaN(n) ? null : n;
    }

    function call(name) {
      if (typeof window[name] === 'function') {
        try { window[name](); } catch (e) {}
      }
    }

    function setCount(n) {
      if (typeof window.btyCartItemCount === 'function') window.btyCartItemCount(n);
      document.querySelectorAll('.iau-drawer-badge').forEach(function (el) {
        if (n > 0) el.removeAttribute('data-empty');
        else el.setAttribute('data-empty', '');
      });
    }

    // Pull the drawer markup again and put the handlers back on it.
    function pull(cart) {
      return fetch(root + '?sections=side-cart,cart-count', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (!data || typeof window.btyGetSectionHtml !== 'function') return;
          var open = document.documentElement.classList.contains('side-cart-open');
          var inner = drawer.querySelector('.side-cart-inner');
          var fresh = data['side-cart'] ? window.btyGetSectionHtml(data['side-cart'], '.side-cart-inner') : '';
          if (inner && fresh) {
            inner.innerHTML = fresh;
            call('btyQuantityButton');
            call('btyUpdateProductQuantity');
            call('btyAgreeToTerms');
            call('btyAnimationImageLoad');
            call('btyScrollAnimationTrigger');
            call('btyMinicartRecommendations');
            call('btySideCartPopupOuter');
            if (typeof window.btyUpdateProgressBarCart === 'function') {
              try { window.btyUpdateProgressBarCart(cart.total_price); } catch (e) {}
            }
            // The close button and overlay are part of the markup we just replaced.
            if (open && typeof window.btyClosePopup === 'function') {
              try { window.btyClosePopup('side-cart-open', drawer); } catch (e) {}
            }
          }
          setCount(cart.item_count);
          known = cart.total_price;
        });
    }

    function sync() {
      if (busy) return;
      // Never pull the markup out from under someone part-way through editing a line.
      if (drawer.querySelector('.updating')) return;
      var active = document.activeElement;
      if (active && drawer.contains(active) && /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName)) return;

      busy = true;
      fetch(cartUrl + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          if (!cart) return;
          var shown = badgeCount();
          var stale = (shown !== null && cart.item_count !== shown) || (known !== null && cart.total_price !== known);
          if (!stale) { known = cart.total_price; return; }
          return pull(cart);
        })
        .catch(function () {})
        .then(function () { busy = false; });
    }

    // Coming back to a page the browser had parked: its cart markup is from before.
    window.addEventListener('pageshow', function (e) { if (e.persisted) sync(); });
    var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    if (nav && nav.type === 'back_forward') sync();

    // In-app browsers (Messenger, Instagram) freeze pages rather than reload them.
    document.addEventListener('visibilitychange', function () { if (!document.hidden) sync(); });

    // And check once more the moment anyone reaches for the cart.
    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('.action-cart, a[href$="/cart"], .iau-drawer-icon')) sync();
    }, true);

    sync();
  }

  function init() {
    initReveal();
    initRails();
    initHero();
    initHeroSlider();
    initCartSync();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme editor: re-run when a section is added or changed.
  document.addEventListener('shopify:section:load', function (e) {
    initReveal(e.target);
    initRails(e.target);
    initHero();
  });
})();

/* ==========================================================================
   Transparent product photos: no multiply blend.
   Opaque white-background photos need mix-blend-mode: multiply to sit on the
   stone card colour, but on a transparent cut-out multiply makes white shoes
   vanish. Detect real transparency per image and tag it (CSS in iau-custom.css).
   ========================================================================== */
(function () {
  var cache = {};
  function check(img) {
    var src = img.currentSrc || img.src;
    if (!src || src.indexOf('data:') === 0 || img.naturalWidth < 8) return;
    if (img.dataset.iauAlphaSrc === src) return;
    img.dataset.iauAlphaSrc = src;
    if (cache[src] !== undefined) { if (cache[src]) img.dataset.iauAlpha = '1'; else delete img.dataset.iauAlpha; return; }
    var probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onload = function () {
      try {
        var c = document.createElement('canvas');
        c.width = c.height = 12;
        var x = c.getContext('2d', { willReadFrequently: true });
        x.drawImage(probe, 0, 0, 12, 12);
        var d = x.getImageData(0, 0, 12, 12).data;
        var corners = [0, 11, 132, 143];
        var t = corners.filter(function (p) { return d[p * 4 + 3] < 40; }).length;
        cache[src] = t >= 3;
      } catch (e) { cache[src] = false; }
      if (cache[src] && (img.currentSrc || img.src) === src) img.dataset.iauAlpha = '1';
    };
    probe.src = src;
  }
  function scan(root) {
    (root || document).querySelectorAll('.product-card .product-image img, .main-product .media-preview-wrap img, .main-product .thumb-view img').forEach(function (img) {
      if (img.complete) check(img);
    });
  }
  document.addEventListener('load', function (e) {
    var t = e.target;
    if (t && t.tagName === 'IMG' && t.closest && t.closest('.product-card, .main-product')) check(t);
  }, true);
  document.addEventListener('DOMContentLoaded', function () { scan(); });
  window.addEventListener('load', function () { scan(); });
  new MutationObserver(function () { scan(); }).observe(document.documentElement, { childList: true, subtree: true });
})();
